import { useState } from 'react'
import { passkeyNodes, passkeyRemoval, passkeysSupported, passkeySubmission } from '../api/passkeys'
import { type FlowSubmission, type KratosFlow, type KratosNode } from '../api/flows'
import { useI18n } from '../i18n'
import { translateKratosMessage } from '../i18n/kratos'

interface PasskeyFormProps {
  flow: KratosFlow
  kind: 'login' | 'settings'
  busy: boolean
  onSubmit: (data: FlowSubmission) => Promise<void>
}

export function PasskeyForm({ flow, kind, busy, onSubmit }: PasskeyFormProps) {
  const { t, locale } = useI18n()
  const [confirm, setConfirm] = useState<KratosNode | null>(null)
  const nodes = passkeyNodes(flow)
  const supported = passkeysSupported()
  const trigger = nodes.find(node => node.attributes.name === (kind === 'login' ? 'passkey_login_trigger' : 'passkey_register_trigger'))
  const credentials = nodes.filter(node => node.attributes.name === 'passkey_remove' && node.attributes.type === 'submit')
  const execute = () => onSubmit(signal => passkeySubmission(flow, kind === 'login' ? 'login' : 'register', signal))
  let actionLabel = kind === 'login' ? 'Sign in with a Passkey' : 'Add Passkey'
  if (busy) actionLabel = 'Please wait…'

  return (
    <div className="space-y-4" aria-busy={busy}>
      <p className="text-sm text-gray-600">{t(kind === 'login'
        ? 'Use a Passkey saved on your device or password manager.'
        : 'Passkeys are optional. Your password and email recovery remain available.')}</p>
      {kind === 'settings' && credentials.length === 0 && <p className="text-sm text-gray-600">{t('No Passkeys added yet.')}</p>}
      {kind === 'settings' && credentials.length > 0 && <ul className="space-y-4">
        {credentials.map((node, index) => {
          const metadata = node.meta?.label?.context
          const name = typeof metadata?.display_name === 'string' && metadata.display_name !== 'unnamed'
            ? metadata.display_name : t('Passkey {number}', { number: index + 1 })
          const addedAt = typeof metadata?.added_at === 'string' ? new Date(metadata.added_at) : null
          const created = addedAt && Number.isFinite(addedAt.getTime()) ? addedAt.toLocaleDateString(locale) : null
          return <li key={String(node.attributes.value)} className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1 break-words">
                <p className="text-sm font-medium">{name}</p>
                {created && <p className="text-xs text-gray-500">{t('Added {date}', { date: created })}</p>}
              </div>
              <button type="button" disabled={busy || node.attributes.disabled}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={t('Remove Passkey {name}', { name })} onClick={() => setConfirm(node)}>{t('Remove')}</button>
            </div>
            {node.attributes.disabled && <p className="text-sm text-gray-600">{t('Add another sign-in method before removing your last Passkey.')}</p>}
            {confirm === node && <div className="space-y-3" role="group" aria-label={t('Remove Passkey {name}', { name })}>
              <p className="text-sm text-gray-600">{t('Remove this Passkey from your account? You can add it again later.')}</p>
              <div className="flex flex-wrap gap-3">
                <button type="button" disabled={busy} className="btn-secondary disabled:opacity-50" onClick={() => {
                  void onSubmit(async () => passkeyRemoval(flow, node))
                  setConfirm(null)
                }}>{t('Confirm removal')}</button>
                <button type="button" disabled={busy} className="btn-secondary disabled:opacity-50" onClick={() => setConfirm(null)}>{t('Cancel')}</button>
              </div>
            </div>}
          </li>
        })}
      </ul>}
      {nodes.flatMap((node, index) => (node.messages || []).map((message, messageIndex) => (
        <p key={`${index}-${messageIndex}`} className="text-sm text-red-600" role="alert">{translateKratosMessage(message, t)}</p>
      )))}
      {trigger && <button type="button" name={trigger.attributes.name}
        disabled={busy || trigger.attributes.disabled || !supported} onClick={() => { void execute() }}
        className="w-full btn-secondary disabled:opacity-50 disabled:cursor-not-allowed">
        {busy && <span className="auth-spinner" aria-hidden="true" />}
        {t(actionLabel)}
      </button>}
      {!supported && <p className="text-sm text-gray-600" role="status">{t('This browser cannot use Passkeys here. You can still use your password.')}</p>}
    </div>
  )
}
